<template>
  <Modal name="CREATE_EDIT_VENDOR_MODAL" :title="title" :width="500" actions>
    <template v-slot:body>
      <v-form ref="vendorForm" v-model="valid" lazy-validation>
        <p class="body-1 font-weight-bold">
          {{ $t('vendors.label.details') }}
        </p>

        <v-text-field
          v-model="name"
          :label="$t('vendors.label.name')"
          :rules="[rules.required]"
          :loading="isVendorLoading"
          hide-details="auto"
          class="mb-5"
          outlined
        />

        <HDocumentID
          v-model="document"
          :default-value="document"
          :loading="isVendorLoading"
          class="mb-5"
        />

        <v-text-field
          v-model="email"
          :label="$t('vendors.label.email')"
          :rules="[rules.email]"
          :loading="isVendorLoading"
          hide-details="auto"
          class="mb-5"
          outlined
        />

        <v-text-field
          v-model="financialEmail"
          :label="$t('vendors.label.financialEmail')"
          :rules="[rules.emailCheckPassEmpty]"
          :loading="isVendorLoading"
          hide-details="auto"
          class="mb-5"
          outlined
        />

        <HPhone
          v-model="hostServicePhone"
          :default-value="hostServicePhone"
          :loading="isVendorLoading"
          :label="$t('vendors.label.hostServicePhone')"
        />

        <HPhone
          v-model="customerServicePhone"
          :default-value="customerServicePhone"
          :loading="isVendorLoading"
          :label="$t('vendors.label.customerServicePhone')"
        />

        <v-textarea
          v-model="description"
          :label="$t('vendors.label.description')"
          :loading="isVendorLoading"
          class="mt-3"
          rows="3"
          auto-grow
          outlined
        />

        <HostSelect
          v-model="host"
          :rules="[rules.required]"
          :dense="false"
        />

        <p class="body-1 mt-5 mb-0 font-weight-bold">
          {{ $t('vendors.label.paymentInfo') }}
        </p>

        <v-row>
          <v-col cols="5">
            <v-select
              v-model="transferMethod"
              @change="resetTransferMethod"
              :label="$t('payment.label.transferMethod')"
              :items="transferMethodPreference"
              :loading="isVendorLoading"
              :rules="[rules.required]"
              hide-details="auto"
              item-value="value"
              item-text="name"
              outlined
            />
          </v-col>
          <v-col cols="7">
            <v-select
              v-if="isPreferredPaymentMethodPIX"
              v-model="pixKeyType"
              :label="$t('payment.label.pixKeyType')"
              :loading="isVendorLoading"
              :rules="[rules.required]"
              :items="pixType"
              hide-details="auto"
              item-value="value"
              item-text="name"
              outlined
            />
            <v-text-field
              v-if="isPreferredPaymentMethodASAAS"
              v-model="walletId"
              :label="$t('payment.label.walletId')"
              :loading="isVendorLoading"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-text-field
              v-if="hasPixKeyType"
              v-model="pixKey"
              v-mask="handleRulesPixKey(pixKeyType).mask"
              :label="$t('payment.label.pixKey')"
              :rules="handleRulesPixKey(pixKeyType).rule"
              :loading="isVendorLoading"
              outlined
            />
          </v-col>
        </v-row>

        <p class="body-1 mt-5 mb-0 font-weight-bold">
          {{ $t('vendors.label.vendorLogo') }}
        </p>

        <v-card class="grey lighten-3" elevation="0">
          <img
            ref="logoPreview"
            class="d-block mx-auto rounded-lg mb-5"
            width="240"
          />
        </v-card>

        <HUpload
          @change="handleVendorsLogo"
          :disabled="isVendorLoading"
          class="my-0"
          multiple
          small
        />
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="isVendorLoading"
        elevation="0"
        class="mr-5"
        large
        text
      >
        {{ $t('vendors.action.cancel') }}
      </v-btn>

      <v-btn
        @click="createVendor"
        :loading="isVendorLoading"
        color="primary"
        elevation="0"
        large
      >
        {{ vendor ? $t('vendors.action.update') : $t('vendors.action.save') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import HUpload from '@/components/lib/HUpload'
import HUploading from '@/components/app/HUploading'
import HostSelect from '@/components/host/HostSelect'
import HDocumentID from '../../../components/app/HDocumentID'
import HPhone from '../../../components/app/HPhone'

import { cpfCnpj } from '@/util/masks'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { pixType, transferMethodPreference } from '@/util/enums'
import { email, emailCheckPassEmpty, phone, required, documentCheck } from '@/util/errorHandling'

export default {
  name: 'VendorsCreateEdit',

  components: {
    Modal,
    HUpload,
    HUploading,
    HostSelect,
    HDocumentID,
    HPhone,
  },

  computed: {
    ...mapGetters(['vendor', 'vendorLogoPath', 'isVendorLoading']),

    title() {
      if (this.isVendorLoading) return this.$t('vendors.action.loading')

      return this.vendor
        ? this.$t('vendors.action.edit')
        : this.$t('vendors.action.create')
    },

    isPreferredPaymentMethodPIX() {
      return this.transferMethod === 'PIX'
    },

    isPreferredPaymentMethodASAAS() {
      return this.transferMethod === 'ASAAS'
    },

    hasPixKeyType() {
      return this.pixKeyType !== null
    },

    pixNeedToBeCleaned() {
      return this.pixKeyType === 'PHONE' || this.pixKeyType === 'CPF' || this.pixKeyType === 'CNPJ'
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),

    ...mapActions(['createEditVendor']),

    createVendor() {
      if (!this.$refs.vendorForm.validate()) return

      const vendor = {
        id: this.vendor?.id ? this.vendor?.id : null,
        name: this.name,
        email: this.email,
        financial_email: this.financialEmail,
        host_service_phone: this.cleanUp(this.hostServicePhone),
        customer_service_phone: this.cleanUp(this.customerServicePhone),
        document: this.cleanUp(this.document),
        description: this.description,
        fileList: this.fileList ? this.fileList : null,
        host_id: this.host?.id,

        transfer_method_preference: this.transferMethod,

        asaas_wallet_id: this.walletId,
        pix_key_type: this.pixKeyType,
        pix_key: this.pixNeedToBeCleaned ? this.cleanUp(this.pixKey) : this.pixKey,
      }

    this.createEditVendor(vendor)
    },

    cleanUp(value) {
      return value.replace(/[^\d]+/g, '')
    },

    fillFields() {
      this.id = this.vendor.id
      this.name = this.vendor.name
      this.email = this.vendor.email
      this.financialEmail = this.vendor.financial_email
      this.document = this.vendor.document
      this.description = this.vendor.description
      this.hostServicePhone = this.vendor.host_service_phone
      this.customerServicePhone = this.vendor.customer_service_phone
      this.vendor?.logo && this.setVendorsLogo(this.vendor?.logo?.url)
      this.host = this.vendor?.host

      this.transferMethod = this.vendor.transfer_method_preference
      this.pixKeyType = this.vendor.pix_key_type

      this.$nextTick(() => {
        this.pixKey = this.vendor.pix_key
        this.walletId = this.vendor.asaas_wallet_id
      })
    },

    resetFields() {
      this.id = null
      this.name = ''
      this.email = ''
      this.financialEmail = ''
      this.document = ''
      this.description = ''
      this.hostServicePhone = ''
      this.customerServicePhone = ''
      this.host = null
      this.fileList = ''

      this.transferMethod = null
      this.pixKeyType = null
      this.pixKey = null
      this.walletId = null

      this.$refs?.vendorForm?.reset()
    },

    applyDocumentMask() {
      if (!this.document) return this.mask.cpfCnpj.cpf

      return this.document?.replace(/[^\d]+/g, '')?.length <= 11
        ? this.mask.cpfCnpj.cpf
        : this.mask.cpfCnpj.cnpj
    },

    handleVendorsLogo(logo) {
      this.fileList = logo

      if (logo.length) {
        const reader = new FileReader()
        const imgPreview = this.$refs?.logoPreview

        reader.addEventListener('load', function () {
          imgPreview?.setAttribute('src', this.result)
        })

        reader.readAsDataURL(logo[0])
      }
    },

    setVendorsLogo(logo) {
      const imgPreview = this.$refs?.logoPreview
      imgPreview?.setAttribute('src', logo)
    },

    handleRulesPixKey(type) {
      switch(type) {
        case 'EMAIL':
          return { mask: '', rule: [this.rules.email] }
        case 'PHONE':
          return { mask: '(##) #####-####', rule: [this.rules.phone] }
        case 'CPF':
          return { mask: '###.###.###-##', rule: [this.rules.documentCheck] }
        case 'CNPJ':
          return { mask: '##.###.###/####-##', rule: [this.rules.documentCheck] }
        case 'EVP':
          return { mask: '', rule: [this.rules.required] }
        default:
          return { mask: '', rule: [this.rules.required] }
      }
    },

    resetTransferMethod() {
      this.pixKeyType = null
      this.pixKey = null
      this.walletId = null
    }
  },

  watch: {
    vendor(vendor) {
      this.resetFields()

      Promise.resolve(vendor).then(() => {
        if (vendor?.id) this.fillFields()
      })
    },
  },

  data() {
    return {
      id: null,
      name: '',
      email: '',
      financialEmail: '',
      document: '',
      description: '',
      hostServicePhone: '',
      customerServicePhone: '',
      fileList: null,
      host: null,

      transferMethod: null,

      pixKeyType: null,
      pixKey: null,

      walletId: null,

      valid: false,
      mask: { cpfCnpj },

      rules: {
        email,
        phone,
        required,
        documentCheck,
        emailCheckPassEmpty,
      },

      pixType,
      transferMethodPreference,
    }
  },
}
</script>
